/**
 * Index configuration management API operations
 */

import { getApiKey } from '../core/config.js';
import type { HttpClient } from '../core/http.js';
import type {
  AllIndexStatsResponse,
  ApiResponse,
  IndexCapabilities,
  IndexConfig,
  IndexListResponse,
  IndexName,
  IndexOperationResponse,
  IndexStats,
  SearchcraftConfig,
} from '../types/index.js';

/**
 * Index configuration API class
 */
export class IndexApi {
  constructor(
    private readonly config: Readonly<SearchcraftConfig>,
    private readonly httpClient: HttpClient
  ) {}

  /**
   * Lists all index names.
   * Uses GET /index
   * @returns A promise resolving to the list of index names.
   * @throws {ConfigurationError} When `readKey` is not set in the client configuration.
   * @throws {AuthenticationError} When the API key is invalid or lacks read permissions.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async list(): Promise<IndexListResponse> {
    const apiKey = getApiKey(this.config, 'read');
    const path = `${this.config.endpointUrl}/index`;

    const response = await this.httpClient.request<ApiResponse<IndexListResponse>>(
      { method: 'GET', path, timeout: this.config.timeout },
      apiKey
    );

    return response.data.data;
  }

  /**
   * Gets the configuration for a specific index.
   * Uses GET /index/:index
   * @param indexName - The name of the index to retrieve.
   * @returns A promise resolving to the index configuration.
   * @throws {ConfigurationError} When `readKey` is not set in the client configuration.
   * @throws {AuthenticationError} When the API key is invalid or lacks read permissions.
   * @throws {NotFoundError} When the specified index does not exist.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async get(indexName: IndexName): Promise<IndexConfig> {
    const apiKey = getApiKey(this.config, 'read');
    const path = `${this.config.endpointUrl}/index/${indexName}`;

    const response = await this.httpClient.request<ApiResponse<IndexConfig>>(
      { method: 'GET', path, timeout: this.config.timeout },
      apiKey
    );

    return response.data.data;
  }

  /**
   * Creates a new index with the given configuration.
   * Uses POST /index - PUT /index/:index is a replace and 404s for an index
   * that does not exist yet (see {@link replace}). with body `{ index: { name, ...config } }`.
   * The `name` field is automatically populated from the `indexName` parameter.
   * When `ai_enabled` is present in the config, the admin key is used because
   * toggling AI features requires admin-level authorization (engine 0.10.0+).
   * @param indexName - The name of the new index.
   * @param indexConfig - The configuration for the new index.
   * @returns A promise resolving to the operation result.
   * @throws {ConfigurationError} When `ingestKey` is not set in the client configuration, or when `ai_enabled` is set and `adminKey` is not configured.
   * @throws {AuthenticationError} When the API key is invalid or lacks write permissions.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async create(
    indexName: IndexName,
    indexConfig: IndexConfig,
    options: { readonly overrideIfExists?: boolean } = {}
  ): Promise<IndexOperationResponse> {
    const apiKey = getApiKey(this.config, indexConfig.ai_enabled === undefined ? 'write' : 'admin');
    const path = `${this.config.endpointUrl}/index`;

    const response = await this.httpClient.request<ApiResponse<IndexOperationResponse>>(
      {
        method: 'POST',
        path,
        body: {
          index: { name: indexName, ...indexConfig },
          override_if_exists: options.overrideIfExists ?? false,
        },
        timeout: this.config.timeout,
      },
      apiKey
    );

    return response.data.data;
  }

  /**
   * Replaces the full schema of an index that already exists.
   * Uses PUT /index/:index. Unlike {@link update} this is a whole-schema
   * replacement, so anything absent from `indexConfig` reverts to its default.
   * `ai_enabled` is preserved unless set explicitly.
   * @param indexName - The name of the index to replace. It must already exist.
   * @param indexConfig - The replacement configuration.
   * @returns A promise resolving to the operation result.
   * @throws {ConfigurationError} When `ingestKey` is not set in the client configuration, or when `ai_enabled` is set and `adminKey` is not configured.
   * @throws {AuthenticationError} When the API key is invalid or lacks write permissions.
   * @throws {NotFoundError} When the specified index does not exist - use {@link create} for a new one.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async replace(indexName: IndexName, indexConfig: IndexConfig): Promise<IndexOperationResponse> {
    const apiKey = getApiKey(this.config, indexConfig.ai_enabled === undefined ? 'write' : 'admin');
    const path = `${this.config.endpointUrl}/index/${indexName}`;

    const response = await this.httpClient.request<ApiResponse<IndexOperationResponse>>(
      {
        method: 'PUT',
        path,
        body: {
          index: { name: indexName, ...indexConfig },
          override_if_exists: true,
        },
        timeout: this.config.timeout,
      },
      apiKey
    );

    return response.data.data;
  }

  /**
   * Updates an existing index with partial configuration changes.
   * Uses PATCH /index/:index with the partial config sent as the raw body
   * (the engine deserializes the body directly into `IndexPatchPayload`,
   * so the payload must NOT be wrapped in an `index` object).
   * When `ai_enabled` is present in the patch, the admin key is used because
   * toggling AI features requires admin-level authorization (engine 0.10.0+).
   *
   * Two limits come from the engine's patch semantics:
   * - `fields` is **not patchable**. The engine carries the existing schema
   *   fields over verbatim, so a `fields` key here is accepted and silently
   *   ignored. Use {@link replace} to change the schema.
   * - `search_fields`, `weight_multipliers` and `language` cannot be *cleared*.
   *   The engine treats an empty value as "leave unchanged", so passing `[]`,
   *   `{}` or `''` is a no-op rather than a reset. Use {@link replace} to drop
   *   them.
   *
   * Everything else follows normal patch semantics: omit a field to leave it
   * alone. `time_decay_field` can be cleared by passing an empty string.
   * @param indexName - The name of the index to update.
   * @param indexConfig - A partial configuration object with the fields to update.
   * @returns A promise resolving to the operation result.
   * @throws {ConfigurationError} When `ingestKey` is not set in the client configuration, or when `ai_enabled` is set and `adminKey` is not configured.
   * @throws {AuthenticationError} When the API key is invalid or lacks write permissions.
   * @throws {NotFoundError} When the specified index does not exist.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async update(
    indexName: IndexName,
    indexConfig: Partial<IndexConfig>
  ): Promise<IndexOperationResponse> {
    const apiKey = getApiKey(this.config, indexConfig.ai_enabled === undefined ? 'write' : 'admin');
    const path = `${this.config.endpointUrl}/index/${indexName}`;

    const response = await this.httpClient.request<ApiResponse<IndexOperationResponse>>(
      {
        method: 'PATCH',
        path,
        body: indexConfig,
        timeout: this.config.timeout,
      },
      apiKey
    );

    return response.data.data;
  }

  /**
   * Deletes an index.
   * Uses DELETE /index/:index
   * @param indexName - The name of the index to delete.
   * @returns A promise resolving to the operation result.
   * @throws {ConfigurationError} When `ingestKey` is not set in the client configuration.
   * @throws {AuthenticationError} When the API key is invalid or lacks write permissions.
   * @throws {NotFoundError} When the specified index does not exist.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async delete(indexName: IndexName): Promise<IndexOperationResponse> {
    const apiKey = getApiKey(this.config, 'write');
    const path = `${this.config.endpointUrl}/index/${indexName}`;

    const response = await this.httpClient.request<ApiResponse<IndexOperationResponse>>(
      { method: 'DELETE', path, timeout: this.config.timeout },
      apiKey
    );

    return response.data.data;
  }

  /**
   * Gets statistics for all indices.
   * Uses GET /index/stats
   * @returns A promise resolving to statistics for every index on the cluster.
   * @throws {ConfigurationError} When `readKey` is not set in the client configuration.
   * @throws {AuthenticationError} When the API key is invalid or lacks read permissions.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async getStats(): Promise<AllIndexStatsResponse> {
    const apiKey = getApiKey(this.config, 'read');
    const path = `${this.config.endpointUrl}/index/stats`;

    const response = await this.httpClient.request<ApiResponse<AllIndexStatsResponse>>(
      { method: 'GET', path, timeout: this.config.timeout },
      apiKey
    );

    return response.data.data;
  }

  /**
   * Gets statistics for a specific index.
   * Uses GET /index/:index_name/stats
   * @param indexName - The name of the index to retrieve statistics for.
   * @returns A promise resolving to the statistics for the specified index.
   * @throws {ConfigurationError} When `readKey` is not set in the client configuration.
   * @throws {AuthenticationError} When the API key is invalid or lacks read permissions.
   * @throws {NotFoundError} When the specified index does not exist.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async getIndexStats(indexName: IndexName): Promise<IndexStats> {
    const apiKey = getApiKey(this.config, 'read');
    const path = `${this.config.endpointUrl}/index/${indexName}/stats`;

    const response = await this.httpClient.request<ApiResponse<IndexStats>>(
      { method: 'GET', path, timeout: this.config.timeout },
      apiKey
    );

    return response.data.data;
  }

  /**
   * Gets AI capability and configuration status for an index.
   * Uses GET /index/:index_name/capabilities
   * Added in engine 0.10.0. The server does not require authentication for
   * this endpoint, but this client always sends the configured read key.
   * @param indexName - The name of the index to inspect.
   * @returns A promise resolving to the AI capability flags for the index.
   * @throws {ConfigurationError} When `readKey` is not set in the client configuration.
   * @throws {NotFoundError} When the specified index does not exist.
   * @throws {ApiError} When the server returns a non-2xx response.
   * @throws {NetworkError} When the request times out or a network failure occurs.
   */
  async getCapabilities(indexName: IndexName): Promise<IndexCapabilities> {
    const apiKey = getApiKey(this.config, 'read');
    const path = `${this.config.endpointUrl}/index/${indexName}/capabilities`;

    const response = await this.httpClient.request<ApiResponse<IndexCapabilities>>(
      { method: 'GET', path, timeout: this.config.timeout },
      apiKey
    );

    return response.data.data;
  }
}
